import type { CSSProperties } from "react";
import { CountryFlag, countryName } from "@nexus/ui";
import {
  IP_BLOCKING_SERIES_CONTRACT,
  motionClass,
  useAnimatedNumber,
  useWidgetMotion,
} from "@nexus/widget-kit";
import type { IpBlockingIsp, IpBlockingSeriesData } from "@nexus/widget-kit";
import {
  firstError,
  MetricLabel,
  MetricValue,
  unwrap,
  Widget,
  WidgetSkeleton,
  WidgetState,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { WidgetDataRequirements, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { StatusHours } from "./StatusHours";

export interface BlockingOverviewConfig {
  title?: string;
  animateValue?: boolean;
  /** Figures under the global ratio: ISP counts (default) or hours per blocking status. */
  detail?: "isps" | "status_hours";
  /** Country flags beside ranked ISPs; shown unless false. */
  showFlags?: boolean;
}

export const DETAILS = [
  { value: "isps", label: "ISPs measured and below 40%" },
  { value: "status_hours", label: "Hours per blocking status" },
];

export const manifest: WidgetManifest = {
  id: "blocking-overview",
  name: "Blocking Overview",
  version: "1.0.0",
  description:
    "Global confirmed-blocking ratio, ISP counts or hours per blocking status, and bottom five ISP rates for IP or domain blocking over the current period.",
  dataRequests: {
    blocking: {
      source: "blocking-overview-ip",
      contract: IP_BLOCKING_SERIES_CONTRACT,
      configurable: true,
      sources: ["blocking-overview-ip", "blocking-overview-domain"],
    },
  },
};

export const defaultConfig: BlockingOverviewConfig = { title: "Blocking Overview" };

export function getDataRequirements(
  config: BlockingOverviewConfig,
): Record<string, WidgetDataRequirements> {
  return {
    blocking: {
      mode: "select",
      fields:
        config.detail === "status_hours" ? ["effectiveness", "statusHours"] : ["effectiveness"],
    },
  };
}
const count = new Intl.NumberFormat("en");
const ratio = new Intl.NumberFormat("en", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const percent = (value: number | null | undefined) =>
  value == null ? "Unavailable" : ratio.format(value);
const clamp = (value: number) => Math.min(1, Math.max(0, value));

/** Presentation bands shared with ISP Blocking Rate, not operational alarms. */
const RATE_OK = 0.7;
const RATE_LOW = 0.4;

function rateColor(value: number | null | undefined): string {
  if (value == null) return "text-muted";
  if (value >= RATE_OK) return "text-status-ok-text";
  if (value >= RATE_LOW) return "text-status-warn-text";
  return "text-status-error-text";
}

/** Rate fill over a neutral track; the fill takes the text color of its band. */
function RateBar({
  value,
  tone,
  thick,
  label,
}: {
  value: number;
  tone: string;
  thick?: boolean;
  label?: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-full bg-border ${thick ? "h-3" : "h-1.5"} ${tone}`}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      <div className="h-full rounded-full bg-current" style={{ width: `${clamp(value) * 100}%` }} />
    </div>
  );
}

function IspFigure({
  label,
  value,
  tone,
  motion,
  delay,
}: {
  label: string;
  value: string;
  tone: string;
  motion: boolean;
  delay: number;
}) {
  return (
    <div
      className={`${motionClass(motion, "nx-rise")} min-w-0`}
      style={{ "--nx-delay": `${delay}ms` } as CSSProperties}
    >
      <dt className="break-words text-metric-caption font-medium text-secondary">{label}</dt>
      <dd className={`mt-1 text-xl font-bold tabular-nums ${tone}`}>{value}</dd>
    </div>
  );
}

function IspRanking({
  title,
  entries,
  motion,
  delay,
  showFlags,
}: {
  title: string;
  entries: IpBlockingIsp[];
  motion: boolean;
  delay: number;
  showFlags: boolean;
}) {
  return (
    <section className="flex min-w-0 flex-1 flex-col gap-3" aria-label="ISP blocking rates">
      <h3 className="text-metric-label font-semibold text-primary">{title}</h3>
      {entries.length ? (
        <ol className="flex flex-1 flex-col justify-between">
          {entries.map((item, index) => {
            const country = countryName(item.country);
            const counts = `${count.format(item.blockedSendings)} / ${count.format(item.eligibleSendings)} approved ticket/ISP sendings confirmed blocked`;
            return (
              <li
                key={item.isp}
                className={`${motionClass(motion, "nx-rise")} grid min-w-0 items-center gap-x-2 py-1.5 wall-short:py-1 @md/widget:gap-x-1.5 @lg/widget:gap-x-3 ${
                  showFlags
                    ? "grid-cols-[auto_minmax(0,1fr)_max-content] @md/widget:grid-cols-[auto_minmax(0,1.75fr)_minmax(1rem,1fr)_max-content] @lg/widget:grid-cols-[auto_minmax(0,1.75fr)_minmax(2rem,1fr)_max-content]"
                    : "grid-cols-[minmax(0,1fr)_max-content] @md/widget:grid-cols-[minmax(0,1.75fr)_minmax(1rem,1fr)_max-content] @lg/widget:grid-cols-[minmax(0,1.75fr)_minmax(2rem,1fr)_max-content]"
                }`}
                style={{ "--nx-delay": `${delay + index * 45}ms` } as CSSProperties}
                title={`${item.isp} (${country ?? "country unavailable"}): ${counts}`}
              >
                {showFlags && (
                  <CountryFlag country={item.country} className="h-3.5 w-[1.1875rem]" />
                )}
                <span className="min-w-0 truncate text-metric-label font-medium text-primary">
                  {item.isp}
                </span>
                {/* The figure carries the rate; in a narrow widget the name needs this room more. */}
                <span
                  aria-hidden="true"
                  className="hidden h-1.5 overflow-hidden rounded-full bg-border @md/widget:block"
                >
                  <span
                    className={`block h-full rounded-full bg-current ${rateColor(item.ratio)}`}
                    style={{ width: `${clamp(item.ratio ?? 0) * 100}%` }}
                  />
                </span>
                <span
                  className={`text-right text-metric-label font-semibold tabular-nums ${rateColor(item.ratio)}`}
                >
                  {percent(item.ratio)}
                </span>
                <span className="sr-only">
                  {country ? `${country}. ` : "Country unavailable. "}
                  {count.format(item.blockedSendings)} of {count.format(item.eligibleSendings)}{" "}
                  approved ticket/ISP sendings confirmed blocked
                </span>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="py-2 text-metric-detail text-muted">No eligible ISP sendings</p>
      )}
    </section>
  );
}

export function Component({ data, config }: WidgetProps<BlockingOverviewConfig>) {
  const report = unwrap<IpBlockingSeriesData>(data.blocking);
  const error = firstError(data, ["blocking"]);
  const effectiveness = report?.effectiveness;
  const total = effectiveness?.total;
  const motion = useWidgetMotion();
  const shownRatio = useAnimatedNumber(total?.ratio, {
    duration: 1100,
    enabled: motion && config.animateValue === true,
  });
  const ranked = [...(effectiveness?.isps ?? [])]
    .filter((item) => item.ratio != null && item.eligibleSendings > 0)
    .sort(
      (first, second) =>
        (first.ratio ?? 0) - (second.ratio ?? 0) ||
        first.blockedSendings - second.blockedSendings ||
        first.isp.localeCompare(second.isp),
    );
  const lowCount = ranked.filter((item) => (item.ratio ?? 0) < RATE_LOW).length;
  const countsTitle = total
    ? `${count.format(total.blockedSendings)} / ${count.format(total.eligibleSendings)} approved ticket/ISP sendings confirmed blocked`
    : undefined;
  return (
    <Widget title={config.title?.trim() || defaultConfig.title} intrinsicHeight>
      {data.blocking?.isLoading && !report ? (
        <WidgetSkeleton shape="kpi" />
      ) : error ? (
        <WidgetIssueState error={error} />
      ) : !effectiveness ? (
        <WidgetState title="Blocking data unavailable" />
      ) : (
        <div className="density-metric-padding grid min-w-0 flex-1 content-start gap-x-4 gap-y-4 wall-short:gap-y-3 @min-[22rem]/widget:grid-cols-[minmax(0,4fr)_minmax(0,5fr)] @lg/widget:grid-cols-2 @lg/widget:gap-x-6">
          <div className="flex min-w-0 flex-col gap-4 wall-short:gap-3">
            <div className="min-w-0">
              <MetricValue size="headline" title={countsTitle}>
                <span className={rateColor(total?.ratio)}>
                  {total?.ratio == null ? "—" : percent(shownRatio ?? total.ratio)}
                </span>
              </MetricValue>
              <MetricLabel className="mt-1">
                {total?.ratio == null ? (
                  "Global blocking ratio unavailable"
                ) : (
                  <>
                    <span className="wall-short:hidden">Global blocking ratio</span>
                    <span className="hidden wall-short:inline">Global ratio</span>
                  </>
                )}
              </MetricLabel>
              {total && (
                <span className="sr-only">
                  {count.format(total.blockedSendings)} of {count.format(total.eligibleSendings)}{" "}
                  approved ticket/ISP sendings confirmed blocked
                </span>
              )}
            </div>
            {total?.ratio != null && (
              <div className="space-y-2" title={countsTitle}>
                <RateBar
                  value={shownRatio ?? total.ratio}
                  tone={rateColor(total.ratio)}
                  thick
                  label={`Blocked ${percent(total.ratio)}; not blocked ${percent(1 - total.ratio)}`}
                />
                {/* Repeats the headline: the first detail a short wall screen drops. */}
                <div className="flex flex-wrap justify-between gap-x-4 text-metric-detail wall-short:hidden">
                  <span className="flex items-center gap-1.5 font-semibold text-secondary">
                    <span
                      aria-hidden
                      className={`size-2 rounded-full bg-current ${rateColor(total.ratio)}`}
                    />
                    Blocked {percent(total.ratio)}
                  </span>
                  <span className="flex items-center gap-1.5 text-muted">
                    <span aria-hidden className="size-2 rounded-full bg-border-strong" />
                    Not blocked {percent(1 - total.ratio)}
                  </span>
                </div>
              </div>
            )}
            {config.detail === "status_hours" ? (
              <StatusHours entries={report?.statusHours} motion={motion} delay={160} />
            ) : (
              <dl className="grid min-w-0 grid-cols-2 gap-x-4 gap-y-4 pt-2 wall-short:sr-only">
                <IspFigure
                  label="ISPs measured"
                  value={count.format(ranked.length)}
                  tone="text-primary"
                  motion={motion}
                  delay={160}
                />
                <IspFigure
                  label="Below 40%"
                  value={count.format(lowCount)}
                  tone={lowCount ? "text-status-error-text" : "text-primary"}
                  motion={motion}
                  delay={240}
                />
              </dl>
            )}
          </div>
          <div className="flex min-w-0 flex-col border-t border-border pt-3 @min-[22rem]/widget:border-t-0 @min-[22rem]/widget:border-l @min-[22rem]/widget:pt-0 @min-[22rem]/widget:pl-4 @lg/widget:pl-6">
            <IspRanking
              title="Bottom 5 ISPs"
              entries={ranked.slice(0, 5)}
              motion={motion}
              delay={105}
              showFlags={config.showFlags !== false}
            />
          </div>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<BlockingOverviewConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements,
};
export default widgetModule;
